/**
 * One task and everything on it — Tasks module, Epic 4 (US 4.1–4.14).
 *
 * Every task a person can open goes through `/hub-tasks/<id>/` — Hub tasks
 * and private tasks alike — so the side panel and the full page read and
 * write exactly the same thing (US 4.3).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_CALENDAR_PREFIX, SuiteApiClient, getValidAccessTokenFromStorage } from "@weco/suite-client";
import { TASKS_QUERY_KEY } from "../api";
import { HubApiError, HUBS_KEY, type BoardStatus, type HubPerson } from "../hubs/api";

const client = new SuiteApiClient(API_CALENDAR_PREFIX);

export type Priority = "urgent" | "high" | "medium" | "low";
export type Category = "" | "design" | "product" | "ops" | "finance" | "exec";
export type TaskType = "task" | "bug" | "milestone";

export const PRIORITIES: Priority[] = ["urgent", "high", "medium", "low"];
export const CATEGORIES: Exclude<Category, "">[] = ["design", "product", "ops", "finance", "exec"];
export const TASK_TYPES: TaskType[] = ["task", "bug", "milestone"];

export interface RepeatRule {
  kind: "daily" | "workday" | "weekly" | "monthly" | "yearly" | "custom";
  days?: number[];
  mode?: "date" | "pattern";
  day?: number;
  nth?: number;
  weekday?: number;
  interval?: number;
  unit?: "day" | "week" | "month";
}

export interface Label {
  id: string;
  name: string;
}

/** A linked task (US 5.4). One the viewer can't open is only "Restricted task". */
export interface TaskLink {
  id: string | null;
  key: string;
  title: string | null;
  done: boolean;
  restricted: boolean;
  /** The dependency itself, for removing it. */
  link: string;
}

/** A task as cards and rows see it. */
export interface TaskCard {
  id: string;
  /** "FIN-12" (US 5.4, searchable in US 6.4). */
  key?: string;
  /** US 5.4 — tasks this one waits on, and whether any is still open. */
  blocked_by?: TaskLink[];
  blocked?: boolean;
  /** US 5.5 — the latest approval request. */
  approval?: { id: string; status: "pending" | "approved" | "rejected" | "cancelled"; changed_after: boolean } | null;
  /** US 6.2 — order within a column (smaller first). */
  position?: number;
  /** US 7.2 — the sprint it is planned into (null = "Not in a sprint"), and its place there. */
  sprint?: string | null;
  sprint_position?: number;
  hub: string | null;
  squad: string | null;
  squad_name: string;
  private: boolean;
  personal_list: string | null;
  title: string;
  description: string;
  status: BoardStatus;
  done: boolean;
  priority: Priority;
  category: Category;
  type: TaskType;
  start_date: string | null;
  start_time: string | null;
  due_date: string | null;
  due_time: string | null;
  time_zone: string;
  assignees: HubPerson[];
  labels: Label[];
  subtask_count: number;
  subtask_done: number;
  first_open_subtask: string;
  attachment_count: number;
  comment_count: number;
  watcher_count: number;
  reminder: { remind_at: string; sent: boolean } | null;
  repeat_rule: RepeatRule | null;
  /** US 14.1 — estimate in minutes. */
  time_estimate?: number | null;
  series_id: string | null;
  custom_values: Record<string, unknown>;
  archived: boolean;
  deleted: boolean;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
  updated_by_email: string;
}

export interface Subtask {
  id: string;
  title: string;
  done: boolean;
}

export interface Attachment {
  id: string;
  name: string;
  size: number;
  content_type: string;
  uploaded_by: string;
  uploaded_by_email: string;
  created_at: string;
}

/** A task as the detail panel / full page sees it. */
export interface TaskFull extends TaskCard {
  subtasks: Subtask[];
  attachments: Attachment[];
  watchers: HubPerson[];
  watching: boolean;
  can_edit: boolean;
  can_admin: boolean;
  hub_name: string;
  personal_list_name: string;
}

export interface Comment {
  id: string;
  parent: string | null;
  author: string;
  author_email: string;
  body: string;
  mentions: HubPerson[];
  edited: boolean;
  deleted: boolean;
  created_at: string;
  /** US 9.3 — a comment assigned to someone, and whether it's resolved. */
  assignee?: HubPerson | null;
  assigned_by?: string | null;
  resolved?: boolean;
  resolved_at?: string | null;
}

export interface ActivityRow {
  id: number;
  actor: string;
  actor_email: string;
  kind: string;
  field: string;
  old: string;
  new: string;
  via: string;
  at: string;
}

export interface PersonalList {
  id: string;
  name: string;
  open_count: number;
}

export type FieldType = "text" | "number" | "date" | "dropdown" | "checkbox" | "person";
export const FIELD_TYPES: FieldType[] = ["text", "number", "date", "dropdown", "checkbox", "person"];

export interface SquadField {
  id: string;
  squad: string;
  name: string;
  type: FieldType;
  options: string[];
  required: boolean;
  position: number;
  value_count: number;
}

export interface TasksSettings {
  /** 0 = Sunday, 1 = Monday, 6 = Saturday. */
  week_start: number;
  workweek: "sun_thu" | "mon_fri";
  /** Only the Workspace Owner changes these; everyone else reads them. */
  can_edit?: boolean;
}

/** What a PATCH may carry besides plain fields. */
export interface TaskPatchBody {
  title?: string;
  description?: string;
  /** A status key of the task's Squad (US 5.2). */
  status?: BoardStatus | (string & {});
  /** US 6.2 — reorder within a column. */
  position?: number;
  /** US 7.2 — plan into a sprint (null = "Not in a sprint") at this place. */
  sprint?: string | null;
  sprint_position?: number;
  priority?: Priority;
  category?: Category;
  type?: TaskType;
  start_date?: string | null;
  start_time?: string | null;
  due_date?: string | null;
  due_time?: string | null;
  time_zone?: string;
  assignees?: string[];
  add_to_squad?: boolean;
  labels?: string[];
  repeat_rule?: RepeatRule | null;
  scope?: "this" | "future";
  complete_subtasks?: boolean;
  custom_values?: Record<string, unknown>;
  squad?: string | null;
  private?: boolean;
  personal_list?: string | null;
  /** US 14.1 — minutes; null clears it. */
  time_estimate?: number | null;
}

export interface NewTaskBody extends TaskPatchBody {
  title: string;
  subtasks?: string[];
}

/** An API error with the server's own message, code and any extra fields. */
export class TaskApiError extends HubApiError {
  extra: Record<string, unknown>;
  status?: number;
  constructor(message: string, code: string, extra: Record<string, unknown> = {}, status?: number) {
    super(message, code);
    this.extra = extra;
    this.status = status;
  }
}

/** Runs a request and turns any failure into a TaskApiError. */
export async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    const e = err as { details?: Record<string, unknown> & { detail?: string; code?: string }; message?: string; code?: number };
    if (!e || typeof e !== "object" || !("details" in e || "code" in e)) throw new TaskApiError("You're offline.", "network");
    const d = (e.details ?? {}) as Record<string, unknown> & { detail?: string; code?: string };
    throw new TaskApiError(d.detail || e.message || "Request failed", d.code || "", d, typeof e.code === "number" ? e.code : undefined);
  }
}

/** The viewer's own time zone, sent with every date a task gets (US 4.2). */
export const myTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

export const taskApi = {
  get: (id: string) => call(() => client.get<TaskFull>(`/hub-tasks/${id}/`)),
  update: (id: string, patch: TaskPatchBody) => call(() => client.patch<TaskFull>(`/hub-tasks/${id}/`, patch)),
  remove: (id: string) => call(() => client.delete<{ deleted: boolean; subtasks: number; attachments: number }>(`/hub-tasks/${id}/`)),
  action: (id: string, action: "duplicate" | "archive" | "unarchive" | "restore") =>
    call(() => client.post<TaskFull>(`/hub-tasks/${id}/${action}/`, {})),
  createInHub: (hubId: string, body: NewTaskBody) => call(() => client.post<TaskFull>(`/hubs/${hubId}/tasks/`, { time_zone: myTimeZone(), ...body })),
  createPrivate: (body: NewTaskBody) => call(() => client.post<TaskFull>("/private-tasks/", { time_zone: myTimeZone(), ...body })),
  privateTasks: (listId?: string, archived = false) =>
    call(() =>
      client.get<{ results: TaskCard[] }>(`/private-tasks/?${new URLSearchParams({ ...(listId ? { list: listId } : {}), ...(archived ? { archived: "1" } : {}) })}`),
    ).then((r) => r.results),
  people: (id: string, q = "") =>
    call(() => client.get<{ results: HubPerson[] }>(`/hub-tasks/${id}/people/${q ? `?q=${encodeURIComponent(q)}` : ""}`)).then((r) => r.results),
  addSubtask: (id: string, title: string) => call(() => client.post<TaskFull>(`/hub-tasks/${id}/subtasks/`, { title })),
  updateSubtask: (subId: string, patch: { title?: string; done?: boolean }) => call(() => client.patch<TaskFull>(`/task-subtasks/${subId}/`, patch)),
  removeSubtask: (subId: string) => call(() => client.delete<TaskFull>(`/task-subtasks/${subId}/`)),
  attachmentUrl: (attachmentId: string) => `${API_CALENDAR_PREFIX}/task-attachments/${attachmentId}/`,
  removeAttachment: (attachmentId: string) => call(() => client.delete<void>(`/task-attachments/${attachmentId}/`)),
  comments: (id: string) => call(() => client.get<{ results: Comment[] }>(`/hub-tasks/${id}/comments/`)).then((r) => r.results),
  addComment: (id: string, body: string, mentions: string[], parent?: string | null) =>
    call(() => client.post<Comment>(`/hub-tasks/${id}/comments/`, { body, mentions, parent: parent ?? null })),
  editComment: (commentId: string, body: string) => call(() => client.patch<Comment>(`/task-comments/${commentId}/`, { body })),
  removeComment: (commentId: string) => call(() => client.delete<void>(`/task-comments/${commentId}/`)),
  /** US 9.3 — assign a comment to someone who can open the task (null clears it). */
  assignComment: (commentId: string, email: string | null) => call(() => client.post<unknown>(`/task-comments/${commentId}/assign/`, { email: email ?? "" })),
  resolveComment: (commentId: string, resolved: boolean) => call(() => client.post<unknown>(`/task-comments/${commentId}/${resolved ? "resolve" : "unresolve"}/`, {})),
  activity: (id: string) => call(() => client.get<{ results: ActivityRow[] }>(`/hub-tasks/${id}/activity/`)).then((r) => r.results),
  watch: (id: string, on: boolean) => call(() => (on ? client.post<TaskFull>(`/hub-tasks/${id}/watch/`, {}) : client.delete<TaskFull>(`/hub-tasks/${id}/watch/`))),
  addWatcher: (id: string, email: string) => call(() => client.post<TaskFull>(`/hub-tasks/${id}/watchers/`, { email })),
  setReminder: (id: string, remindAt: string) => call(() => client.put<TaskFull>(`/hub-tasks/${id}/reminder/`, { remind_at: remindAt })),
  clearReminder: (id: string) => call(() => client.delete<TaskFull>(`/hub-tasks/${id}/reminder/`)),
  snooze: (id: string, body: { minutes: number } | { until: string }) => call(() => client.post<TaskFull>(`/hub-tasks/${id}/reminder/`, body)),
  labels: (q = "") => call(() => client.get<{ results: Label[] }>(`/task-labels/${q ? `?q=${encodeURIComponent(q)}` : ""}`)).then((r) => r.results),
  lists: () => call(() => client.get<{ results: PersonalList[] }>("/personal-lists/")).then((r) => r.results),
  createList: (name: string) => call(() => client.post<PersonalList>("/personal-lists/", { name })),
  renameList: (id: string, name: string) => call(() => client.patch<PersonalList>(`/personal-lists/${id}/`, { name })),
  removeList: (id: string) => call(() => client.delete<void>(`/personal-lists/${id}/`)),
  settings: () => call(() => client.get<TasksSettings>("/tasks-settings/")),
  updateSettings: (patch: Partial<TasksSettings>) => call(() => client.patch<TasksSettings>("/tasks-settings/", patch)),
  fields: (squadId: string) => call(() => client.get<{ results: SquadField[] }>(`/squads/${squadId}/fields/`)).then((r) => r.results),
  addField: (squadId: string, body: { name: string; type: FieldType; options?: string[]; required?: boolean }) =>
    call(() => client.post<SquadField>(`/squads/${squadId}/fields/`, body)),
  updateField: (fieldId: string, body: Partial<{ name: string; type: FieldType; options: string[]; required: boolean; confirm: boolean }>) =>
    call(() => client.patch<SquadField>(`/squad-fields/${fieldId}/`, body)),
  removeField: (fieldId: string, confirm = false) => call(() => client.delete<void>(`/squad-fields/${fieldId}/${confirm ? "?confirm=1" : ""}`)),
  orderFields: (squadId: string, ids: string[]) => call(() => client.post<{ results: SquadField[] }>(`/squads/${squadId}/fields/order/`, { ids })),
  deletedTasks: (hubId: string) =>
    call(() => client.get<{ results: { id: string; title: string; squad_name: string; deleted_at: string; deleted_by: string }[] }>(`/hubs/${hubId}/deleted-tasks/`)).then((r) => r.results),
};

/** Upload a file with progress (US 4.6). Resolves to the stored file, or
 *  rejects — a failed upload is never shown as attached. */
export async function uploadAttachment(
  taskId: string,
  file: File,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<Attachment> {
  const token = await getValidAccessTokenFromStorage();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_CALENDAR_PREFIX}/hub-tasks/${taskId}/attachments/`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let body: { detail?: string; code?: string } & Partial<Attachment> = {};
      try {
        body = JSON.parse(xhr.responseText || "{}");
      } catch {
        /* not JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as Attachment);
      else reject(new TaskApiError(body.detail || "Upload failed.", body.code || "upload_failed"));
    };
    xhr.onerror = () => reject(new TaskApiError("Upload failed.", "network"));
    xhr.onabort = () => reject(new TaskApiError("Upload cancelled.", "aborted"));
    signal?.addEventListener("abort", () => xhr.abort());
    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  });
}

/** Fetch a stored file with the caller's credentials and hand back an object
 *  URL — an `<img>` or a download link can't send a bearer token itself. */
export async function attachmentBlobUrl(attachmentId: string, inline = true): Promise<string> {
  const blob = await call(() => client.getBlob(`/task-attachments/${attachmentId}/${inline ? "?inline=1" : ""}`));
  return URL.createObjectURL(blob);
}

export const taskKey = (id: string) => ["tasks", "task", id] as const;

export function useTask(id: string | null) {
  return useQuery({ queryKey: taskKey(id ?? ""), queryFn: () => taskApi.get(id!), enabled: !!id, retry: false });
}

export function useComments(id: string | null) {
  return useQuery({ queryKey: [...taskKey(id ?? ""), "comments"], queryFn: () => taskApi.comments(id!), enabled: !!id });
}

export function useActivity(id: string | null) {
  return useQuery({ queryKey: [...taskKey(id ?? ""), "activity"], queryFn: () => taskApi.activity(id!), enabled: !!id });
}

export function usePersonalLists() {
  return useQuery({ queryKey: ["tasks", "personal-lists"], queryFn: () => taskApi.lists() });
}

export function useTasksSettings() {
  return useQuery({
    queryKey: ["tasks", "settings"],
    queryFn: () => taskApi.settings(),
    staleTime: 5 * 60_000,
    placeholderData: { week_start: 0, workweek: "sun_thu" } as TasksSettings,
  });
}

export function useSquadFields(squadId: string | null) {
  return useQuery({ queryKey: ["tasks", "hub", "fields", squadId ?? ""], queryFn: () => taskApi.fields(squadId!), enabled: !!squadId });
}

/** After any task change: the task, every board it can sit on, My tasks. */
export function useRefreshTask() {
  const qc = useQueryClient();
  return (id?: string) => {
    if (id) void qc.invalidateQueries({ queryKey: taskKey(id) });
    void qc.invalidateQueries({ queryKey: HUBS_KEY });
    void qc.invalidateQueries({ queryKey: ["tasks", "hub"] });
    void qc.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: ["tasks", "private"] });
    void qc.invalidateQueries({ queryKey: ["tasks", "personal-lists"] });
  };
}
